// TASK-BUDGETS (core plan item 8): the seat settings' "Task limits" for the DE holding the seat
// (`GET/PUT …/digital-employees/{id}/task-limits`). Each field left blank keeps the company's DE
// default (shown as its placeholder); a filled field is the DE's override, read at its next run's
// admission. "Use company defaults" clears the override. When the company does not enforce task
// limits the notice says so: runs still carry them, unenforced.
import { Button, Field, FieldLabel, Input, Notice } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import { FormError } from "./parts";

type Limits = { steps?: number; cost_micros?: number; time_ms?: number };
type View = {
  membership_id: string;
  enabled: boolean;
  defaults: Required<Limits>;
  override: Limits | null;
  effective: Required<Limits>;
};

export const taskLimitsKey = (co: string, de: string) =>
  ["company", co, "digital-employees", de, "task-limits"] as const;

/** The form's text fields from an override (blank: the default). */
export function fieldsOf(o: Limits | null) {
  return {
    steps: o?.steps ? String(o.steps) : "",
    dollars: o?.cost_micros ? (o.cost_micros / 1_000_000).toFixed(2) : "",
    minutes: o?.time_ms ? String(Math.round(o.time_ms / 60_000)) : "",
  };
}

/** The override from the form's text fields; a blank or invalid field is left unset. */
export function overrideOf(f: { steps: string; dollars: string; minutes: string }): Limits | null {
  const out: Limits = {};
  const steps = Number(f.steps);
  if (f.steps.trim() && Number.isInteger(steps) && steps >= 1) out.steps = steps;
  const dollars = Number(f.dollars);
  if (f.dollars.trim() && Number.isFinite(dollars) && dollars > 0)
    out.cost_micros = Math.round(dollars * 1_000_000);
  const minutes = Number(f.minutes);
  if (f.minutes.trim() && Number.isInteger(minutes) && minutes >= 1) out.time_ms = minutes * 60_000;
  return Object.keys(out).length > 0 ? out : null;
}

export function SeatTaskLimits({
  api,
  co,
  deId,
  readOnly,
}: {
  api: Api;
  co: string;
  deId: string;
  readOnly: boolean;
}) {
  const view = useQuery({
    queryKey: taskLimitsKey(co, deId),
    queryFn: () =>
      api.client.digitalEmployees.taskLimits({ params: { co, id: deId } }) as Promise<View>,
    retry: false,
  });
  if (view.isPending) return <p className="text-sm text-muted-foreground">Loading the limits…</p>;
  if (view.isError) return <FormError what="Loading the task limits" error={view.error} />;
  return (
    <LimitsForm
      key={JSON.stringify(view.data.override)}
      api={api}
      co={co}
      deId={deId}
      view={view.data}
      readOnly={readOnly}
    />
  );
}

function LimitsForm({
  api,
  co,
  deId,
  view,
  readOnly,
}: {
  api: Api;
  co: string;
  deId: string;
  view: View;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const initial = fieldsOf(view.override);
  const [f, setF] = useState(initial);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (override: Limits | null) =>
      api.client.digitalEmployees.putTaskLimits({
        params: { co, id: deId },
        body: { override },
      }) as Promise<View>,
    onSuccess: (next) => {
      setSaved(true);
      qc.setQueryData(taskLimitsKey(co, deId), next);
    },
  });
  const dirty = JSON.stringify(overrideOf(f)) !== JSON.stringify(overrideOf(initial));
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => {
    setSaved(false);
    setF({ ...f, [k]: e.target.value });
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (dirty) save.mutate(overrideOf(f));
  };
  const d = view.defaults;
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" aria-label="Task limits">
      {view.enabled ? null : (
        <Notice tone="neutral">
          Task limits are not enforced for this company yet: runs carry them, but nothing is
          stopped.
        </Notice>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <Field>
          <FieldLabel htmlFor="limit-steps">Tool steps per task</FieldLabel>
          <Input
            id="limit-steps"
            inputMode="numeric"
            placeholder={String(d.steps)}
            value={f.steps}
            disabled={readOnly}
            onChange={set("steps")}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="limit-cost">Cost per task ($)</FieldLabel>
          <Input
            id="limit-cost"
            inputMode="decimal"
            placeholder={(d.cost_micros / 1_000_000).toFixed(2)}
            value={f.dollars}
            disabled={readOnly}
            onChange={set("dollars")}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="limit-time">Minutes per task</FieldLabel>
          <Input
            id="limit-time"
            inputMode="numeric"
            placeholder={String(Math.round(d.time_ms / 60_000))}
            value={f.minutes}
            disabled={readOnly}
            onChange={set("minutes")}
          />
        </Field>
      </div>
      <p className="text-sm text-muted-foreground" data-slot="task-limits-effective">
        A new task gets {view.effective.steps} steps, $
        {(view.effective.cost_micros / 1_000_000).toFixed(2)} and{" "}
        {Math.round(view.effective.time_ms / 60_000)} minutes. Blank fields use the company default.
      </p>
      <FormError what="Saving the task limits" error={save.error} />
      {readOnly ? null : (
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save limits"}
          </Button>
          {view.override ? (
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={() => save.mutate(null)}
            >
              Use company defaults
            </Button>
          ) : null}
          <span role="status" className="text-sm text-muted-foreground">
            {saved && !dirty ? "Limits saved." : ""}
          </span>
        </div>
      )}
    </form>
  );
}
